import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ShieldCheck, Copy, AlertCircle, KeyRound, CheckCircle2 } from 'lucide-react';

/**
 * TOTP enrollment screen — clean EG3 brand chrome (iter114s.68).
 *
 * Replaces the previous medieval theme (knight artwork +
 * Cormorant Garamond italics + "Forge The Royal Seal" copy) with
 * the standard EG3 surface — pale-green gradient backdrop, the
 * shield-check icon, lucide icons, plain conversational copy.
 *
 * Functional logic (httpOnly `eg3_2fa_challenge` cookie, /auth/2fa-context
 * fallback, /totp/enroll-init + /totp/enroll-verify endpoints, eight
 * one-time backup codes, post-enrollment redirect to /admin) is
 * unchanged. All data-testids are preserved.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOTPSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  const [email, setEmail] = useState(location.state?.email || '');
  const [secret, setSecret] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('loading'); // loading | enrolling | verifying | done | error
  const [backupCodes, setBackupCodes] = useState(null);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const initEnrollment = useCallback(async () => {
    try {
      const r = await axios.post(`${API_URL}/api/auth/totp/enroll-init`, {}, { withCredentials: true });
      setSecret(r.data.secret);
      setQrUrl(r.data.qr_data_url);
      if (r.data.account) setEmail(r.data.account);
      setPhase('enrolling');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Something went wrong. Please return to the sign-in page and start again.');
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ctx = await axios.get(`${API_URL}/api/auth/2fa-context`, { withCredentials: true });
        if (cancelled) return;
        if (ctx.data?.email && !email) setEmail(ctx.data.email);
        initEnrollment();
      } catch {
        if (cancelled) return;
        setError('No active enrollment session. Return to the sign-in page first.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [initEnrollment, email]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) {
      setError('Enter exactly 6 digits.');
      return;
    }
    setPhase('verifying');
    try {
      const r = await axios.post(`${API_URL}/api/auth/totp/enroll-verify`, { code }, { withCredentials: true });
      setBackupCodes(r.data.backup_codes || []);
      setPhase('done');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Something went wrong. Please try again.');
      setPhase('enrolling');
    }
  };

  const handleAcknowledge = async () => {
    setAcknowledged(true);
    if (refreshUser) await refreshUser();
    navigate('/admin', { replace: true });
  };

  const copyBackupCodes = () => {
    if (!backupCodes) return;
    try {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 1800);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white flex items-center justify-center py-10 px-4"
      data-testid="totp-setup-page"
    >
      <div className="w-full max-w-xl">
        <div className="text-center mb-5">
          <ShieldCheck className="w-12 h-12 text-[#2F5D3A] mx-auto mb-2" />
          <h1
            className="text-3xl md:text-4xl font-bold text-[#14281D] tracking-tight"
            data-testid="totp-setup-heading"
          >
            {phase === 'done' ? 'Two-factor authentication enabled' : 'Set up two-factor authentication'}
          </h1>
          <p className="mt-2 text-sm md:text-base text-[#1F3D2C]/75">
            {phase === 'done'
              ? 'Save your eight backup codes — each works only once.'
              : 'Add a second verification step so only you can sign in.'}
          </p>
          {email && (
            <p className="text-sm text-gray-500 mt-1" data-testid="totp-setup-account">
              Account: {email}
            </p>
          )}
        </div>

        <div
          className="bg-white rounded-2xl shadow-xl p-6 border border-[#E5E7EB]"
          data-testid="totp-setup-card"
        >
          {error && (
            <div
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm"
              data-testid="totp-setup-error"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {phase === 'loading' && (
            <p className="text-sm text-[#1F3D2C]/75 text-center">
              Preparing your setup…
            </p>
          )}

          {(phase === 'enrolling' || phase === 'verifying') && (
            <div className="space-y-5">
              <ol className="space-y-3 text-sm text-[#14281D] leading-snug">
                <li>
                  <span className="font-semibold text-[#2F5D3A]">1.</span> Open your authenticator app
                  {' '}<span className="text-gray-500">(Google Authenticator, 1Password, Authy, etc.)</span>
                  {' '}and add a new entry.
                </li>
                <li>
                  <span className="font-semibold text-[#2F5D3A]">2.</span> Scan the QR below, or enter the secret manually.
                </li>
                <li>
                  <span className="font-semibold text-[#2F5D3A]">3.</span> Enter the 6-digit code from the app.
                </li>
              </ol>

              <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 items-center bg-[#F4F7F2] p-4 rounded-xl border border-[#E5E7EB]">
                {qrUrl ? (
                  <img
                    src={qrUrl}
                    alt="Scan with your authenticator app"
                    className="w-48 h-48 mx-auto sm:mx-0 rounded-md border-2 border-[#6FBF73]/40 bg-white"
                    data-testid="totp-setup-qr"
                  />
                ) : (
                  <div className="w-48 h-48 mx-auto sm:mx-0 rounded-md bg-emerald-100 animate-pulse" />
                )}
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Or paste this secret manually:</p>
                  <code
                    className="block break-all text-sm bg-white border border-gray-200 rounded px-3 py-2 font-mono text-[#2F5D3A]"
                    data-testid="totp-setup-secret"
                  >
                    {secret || '———'}
                  </code>
                </div>
              </div>

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label
                    className="block text-sm font-medium text-[#14281D] mb-1.5"
                    htmlFor="totp-code"
                  >
                    6-digit code
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                    <input
                      id="totp-code"
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="••••••"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-2xl font-mono tracking-[0.3em] text-center"
                      data-testid="totp-setup-code-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={phase === 'verifying' || code.length !== 6}
                  className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-3 text-base font-semibold rounded-full transition-colors"
                  data-testid="totp-setup-verify-button"
                >
                  {phase === 'verifying' ? 'Verifying…' : 'Verify & enable'}
                </Button>
              </form>
            </div>
          )}

          {phase === 'done' && backupCodes && (
            <div className="space-y-5" data-testid="totp-setup-backup-codes-state">
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 leading-snug">
                  <p className="font-semibold mb-1">Save these codes — shown only once</p>
                  <p>
                    Each code lets you sign in once if you lose access to your authenticator.
                    Store them in a password manager or print them somewhere safe — they won't
                    be shown again.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 bg-[#F4F7F2] p-4 rounded-xl border border-[#E5E7EB]">
                {backupCodes.map((bc, i) => (
                  <code
                    key={bc}
                    className="text-base font-mono text-[#0F2B18] bg-white border border-[#6FBF73]/40 rounded px-3 py-2 text-center select-all"
                    data-testid={`totp-backup-code-${i}`}
                  >
                    {bc}
                  </code>
                ))}
              </div>

              <Button
                type="button"
                onClick={copyBackupCodes}
                variant="outline"
                className="w-full"
                data-testid="totp-setup-copy-button"
              >
                <Copy className="w-4 h-4 mr-2" />
                {copiedCodes ? 'Codes copied to clipboard' : 'Copy all eight codes'}
              </Button>

              <Button
                type="button"
                onClick={handleAcknowledge}
                disabled={acknowledged}
                className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-3 text-base font-semibold rounded-full transition-colors"
                data-testid="totp-setup-acknowledge-button"
              >
                {acknowledged ? 'Proceeding…' : 'I have saved my codes — proceed'}
              </Button>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-center py-3">
              <Link
                to="/login"
                className="inline-block bg-[#6FBF73] hover:bg-[#4A7C59] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
                data-testid="totp-setup-back-to-login"
              >
                Back to sign in
              </Link>
            </div>
          )}
        </div>

        {phase !== 'done' && phase !== 'error' && (
          <div className="text-center mt-4">
            <CheckCircle2 className="w-3 h-3 inline-block text-[#4A7C59] mr-1" />
            <span className="text-sm text-gray-500">
              Your secret never leaves our servers — encrypted at rest, never logged.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TOTPSetup;
